import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import MyGames from './MyGames';
import Login from "./Login.js";
import Logo from "./Logo.js";
import Board from "@sabaki/go-board";
import { parseVertex, parseCompressedVertices } from '@sabaki/sgf'
import BoundedGoban from "./shudan/BoundedGoban.js";
import './App.css';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {treeToJSON} from './Graph.js'
import Graph from './Graph.js'
import NavigationBar from "./NavigationBar";
import ScoreBox from "./ScoreBox";
import PlayerBox from "./PlayerBox.js"
import MoveDetails from "./MoveDetails.js"
import MoveEvaluator from "./MoveEvaluator"
import { useServer } from "./ServerContext";
import { uploadSGF } from "./UploadSGF.js";
import UserPanel from "./UserPanel.js";
import axios from "axios";
import apiClient from "./ApiClient.js";
import { fetchGames } from "./MyGames";
import StoneMenu from "./StoneMenu.js";
import SignUp from "./SignUp.js";
import AnalysisPanel from "./AnalysisPanel.js";
import { savePreferences } from "./Preferences.js";
import RecordGame from "./RecordGame.js";

export const KATAGOSERVER='localhost'
export const KATAGOSERVERPORT='3001'

const sgf = require('@sabaki/sgf')
const GameTree = require('@sabaki/immutable-gametree')
const sha256 = require("js-sha256");


// See 2.11 of https://www.lysator.liu.se/~gunnar/gtp/gtp2-spec-draft2/gtp2-spec.html
export const HORIZONTALCOORDS='ABCDEFGHJKLMNOPQRST' // I is excluded

// Default/empty instances
export const emptyBoard = Board.fromDimensions(19); // empty board

export function blankMap(dimensions = [19,19], fillValue = null) {
  return Array.from({ length: dimensions[1] }, () => Array(dimensions[0]).fill(fillValue));
}

async function fetchUserInfo() {
  try {
    const response = await apiClient.get('/user-info/');
    console.log("User info:", response.data);
    return response.data.username; // Extract username
  } catch (error) {
    console.error("Error fetching user info:", error.response?.data || error.message);
    return null;
  }
}

export function sgfStringToState(SGFSTRING) {
  let gameId = null
  let gameTrees = null
  const defaultGameIndex = 0
  let myState = undefined

  let getId = ((id) => () => id++)(0);
  let rootNodes = sgf.parse(SGFSTRING); // Parse SGF content
  gameTrees = rootNodes.map((rootNode) => {
    return new GameTree({ getId, root: rootNode });
  });

  if (gameTrees?.length == 0) throw true

  let defaultGameCurrents = gameTrees.map(_ => ({}))

  myState = {
    gameTrees: gameTrees,
    gameIndex: defaultGameIndex,
    gameCurrents: defaultGameCurrents,
  }

return myState

}

export function roundToNearestHalf(num) {
  return Math.round(num * 2) / 2;
}

export function getRootProperty(tree, property, fallback = null) {
  let result = ''
  if (property in tree.root.data) result = tree.root.data[property][0]

  return result === '' ? fallback : result
}

export function getGameInfo(tree,currents={}) {

  let komi = getRootProperty(tree, 'KM')

  if (komi != null && !isNaN(komi)) komi = +komi
  else komi = null

  let size = getRootProperty(tree, 'SZ')
  if (size == null) {
    size = [19, 19]
  } else {
    let s = size.toString().split(':')
    size = [+s[0], +s[s.length - 1]]
  }

  let handicap = getRootProperty(tree, 'HA', 0)
  handicap = Math.max(1, Math.min(9, Math.round(handicap)))
  if (handicap === 1) handicap = 0

  let playerNames = ['B', 'W'].map(
    x => getRootProperty(tree, `P${x}`) || getRootProperty(tree, `${x}T`)
  )

  let playerRanks = ['BR', 'WR'].map(x => getRootProperty(tree, x))

  const nodes = [...tree.listCurrentNodes(currents)]
  const rules = tree.root?.data?.['RU']

  // Get game ID. We do *not* use tree.getHash() since we do not
  // want variations on the tree (instigated by the user clicking
  // on the board) to modify the game ID
  let gameAsString = ""
  gameAsString+="komi:"+komi+","+"handicap:"+handicap+","+"rules:"+rules+","+"moves:"
  for(let node of nodes.slice(1)) {
    // console.log(node)
    const nodeToMoveString = node => Object.entries(node.data).map(([key, value]) => key + value[0]).join('');
    gameAsString+=nodeToMoveString(node)
  }
  const gameId = sha256(gameAsString)
  const gameLength = nodes.length - 1

  return {
    playerNames,
    playerRanks,
    blackName: playerNames[0],
    blackRank: playerRanks[0],
    whiteName: playerNames[1],
    whiteRank: playerRanks[1],
    gameName: getRootProperty(tree, 'GN'),
    eventName: getRootProperty(tree, 'EV'),
    gameComment: getRootProperty(tree, 'GC'),
    date: getRootProperty(tree, 'DT'),
    result: getRootProperty(tree, 'RE'),
    komi,
    handicap,
    size,
    gameId: gameId,
    gameLength: gameLength,
  }
}


// Parameters for adjusting which AI suggestions are shown
export const MAXNUMBEROFSUGGESTIONS = 10;
export const MINIMAL_EDGE_WEIGHT_RATIO = .15
export const isAdmissibleSuggestion = (moveInfo, moveData) => {
  // Only take suggestions seriously if they have enough edge weights
  // relative to the first suggested AI move.
  if(!! moveData?.moveInfos) {
    const MAX_EDGE_WEIGHTS = Math.max(...moveData.moveInfos.map(moveInfo => moveInfo.edgeWeight))
    return moveInfo.edgeWeight/MAX_EDGE_WEIGHTS >= MINIMAL_EDGE_WEIGHT_RATIO
  } else { // no other suggested moves
    return true
  }
}

// For analysis we extract the fields which we will actually
// be using. See https://github.com/lightvector/KataGo/blob/master/docs/Analysis_Engine.md
// for the definitions of fields.
export const extractRelevantFieldsByNumber = (myAnalyzedMoves, currentMoveNumber, providedPreviousMovePackage=null) => {

  if (myAnalyzedMoves === undefined || currentMoveNumber === undefined) {
    return null
  }

  const movePackage = myAnalyzedMoves.find(y => y.turn == currentMoveNumber)

  if (movePackage === undefined) {
    return null // This move was not analyzed yet
  }

  return extractRelevantFieldsByElement(movePackage, myAnalyzedMoves, providedPreviousMovePackage)

}

export const extractRelevantFieldsByElement = (movePackage, myAnalyzedMoves, providedPreviousMovePackage=null) => {

  // These data pertain to the last move that was played in
  // the request that was made to Katago which returned moveData.
  // Note that rootInfo.currentPlayer refers to the current player *after*
  // the last move, so if relativeWinrate refers to the last move
  // by Black at move 29, then rootInfo.currentPlayer is white.
  const winrateRelativeToCurrentPlayer = movePackage.data.rootInfo.winrate;
  const currentPlayer = movePackage.data.rootInfo.currentPlayer === 'B' ? 'W' : 'B';
  const nextPlayer = currentPlayer === 'B' ? 'W' : 'B'
  const scoreLeadRelativeToCurrentPlayer = movePackage.data.rootInfo.scoreLead;

  // Explanatory example:
  //
  // Black played a bad game culminating with a bad move at F7 at move 55.
  // There is now only one possible move at A1 for White, after which White
  // will almost surely win.
  //
  // The following request was made to Katago for analysis:
  //
  // { "id":"foo", "moves": [    [ "B", "A5" ],  [ "W", "A4" ],  [ "B", "B5" ],
  // [ "W", "B4" ],  [ "B", "C5" ],  [ "W", "C4" ],    [ "B", "D5" ],  [ "W", "D4" ],
  // [ "B", "E4" ],    [ "W", "E3" ],  [ "B", "F4" ],  [ "W", "F3" ],    [ "B", "G3" ],
  // [ "W", "D3" ],  [ "B", "C3" ],    [ "W", "D2" ],  [ "B", "C2" ],  [ "W", "A2" ],
  // [ "B", "B3" ],  [ "W", "E2" ],  [ "B", "B2" ],    [ "W", "F2" ],  [ "B", "A3" ],
  // [ "W", "E1" ],    [ "B", "C1" ],  [ "W", "D1" ],  [ "B", "B1" ],    [ "W", "F1" ],
  // [ "B", "G2" ],  [ "W", "E5" ],    [ "B", "F5" ],  [ "W", "E6" ],  [ "B", "B6" ],
  // [ "W", "D7" ],  [ "B", "C6" ],  [ "W", "G4" ],    [ "B", "G5" ],  [ "W", "H4" ],
  // [ "B", "J2" ],    [ "W", "Q4" ],  [ "B", "D16" ], [ "W", "Q16" ],
  // [ "B", "O17" ], [ "W", "R14" ], [ "B", "J3" ],    [ "W", "H5" ],  [ "B", "A6" ],
  // [ "W", "D8" ],    [ "B", "B8" ],  [ "W", "E7" ],  [ "B", "G7" ],    [ "W", "P17" ],
  // [ "B", "O16" ], [ "W", "Q17" ],    [ "B", "F7" ]  ],
  // "rules": "tromp-taylor",  "komi": 6.5,  "boardXSize": 19,  "boardYSize": 19 }
  //
  // Katago’s response:
  // {
  //   id: 'foo',
  //   isDuringSearch: false,
  //   moveInfos: [
  //     {
  //       edgeVisits: 462,
  //       edgeWeight: 461.999313,
  //       lcb: 0.988250407,
  //       move: 'A1',
  //       order: 0,
  //       prior: 0.999823987,
  //       pv: [Array],
  //       scoreLead: 20.9703639,
  //       scoreMean: 20.9703639,
  //       scoreSelfplay: 23.4768257,
  //       scoreStdev: 20.6960745,
  //       utility: 1.05345403,
  //       utilityLcb: 1.04914994,
  //       visits: 462,
  //       weight: 461.999313,
  //       winrate: 0.989787581
  //     },
  //     {
  //       edgeVisits: 1,
  //       edgeWeight: 1,
  //       lcb: -1.2259915,
  //       move: 'G6',
  //       order: 1,
  //       prior: 0.00000581619906,
  //       pv: [Array],
  //       scoreLead: -26.3377571,
  //       scoreMean: -26.3377571,
  //       scoreSelfplay: -27.9448357,
  //       scoreStdev: 20.4340498,
  //       utility: -1.21377143,
  //       utilityLcb: -4.71377145,
  //       visits: 1,
  //       weight: 1,
  //       winrate: 0.0240085088
  //     }, … *other irrelevant moves with low edgeweights*
  //   ],
  //   rootInfo: {
  //     currentPlayer: 'W',
  //     rawLead: 19.3240395,
  //     rawNoResultProb: 0,
  //     rawScoreSelfplay: 21.7947464,
  //     rawScoreSelfplayStdev: 21.0401536,
  //     rawStScoreError: -1,
  //     rawStWrError: -0.5,
  //     rawVarTimeLeft: -1,
  //     rawWinrate: 0.973172665,
  //     scoreLead: 20.9668081,
  //     scoreSelfplay: 23.4731927,
  //     scoreStdev: 20.6969711,
  //     symHash: '05867A32AC452DA0710439B1F4C8583B',
  //     thisHash: 'B8E6C66BF5702108C0C70506F6A49001',
  //     utility: 1.05335109,
  //     visits: 504,
  //     weight: 463.0631155806345,
  //     winrate: 0.989751696
  //   },
  //   turnNumber: 55
  // }
  //
  // The fields we defined:
  //   winrateRelativeToCurrentPlayer = movePackage.data.rootInfo.winrate = 0.989751696
  //   currentPlayer = moveData.data.rootInfo.currentPlayer = 'W';
  //   scoreLeadRelativeToCurrentPlayer = moveData.data.rootInfo.scoreLead = 20.9668081;

  // by convention, we'll always measure scores/winrates relative to black
  const blackWinrate = nextPlayer === 'B' ? winrateRelativeToCurrentPlayer * 100 : (1 - winrateRelativeToCurrentPlayer) * 100
  const absoluteScoreRelativeToCurrentPlayer = scoreLeadRelativeToCurrentPlayer * (nextPlayer === 'B' ? 1 : -1)

  // Compute the number of points lost by this move relative to
  // the predicted score lead from last turn's analysis
  let previousScore = undefined
  let previousMaxScoringMove = undefined
  let currentMoveScore = undefined
  let previousMovePackage = null

  if(! !!providedPreviousMovePackage) {
    previousMovePackage = myAnalyzedMoves.find(y => y.turn === movePackage.turn - 1);
  } else {
    previousMovePackage = providedPreviousMovePackage
  }

  if ( !! previousMovePackage ) { // Last move has been analyzed or provided

    // Evaluate value of move made
    const previousRelativeScoreLead = previousMovePackage.data.rootInfo.scoreLead;
    const previousRelativeMaxScoringMove =
        Math.max(...previousMovePackage.data.moveInfos.map(moveInfo => moveInfo.scoreLead))
    previousMaxScoringMove = previousRelativeMaxScoringMove * (currentPlayer === 'B' ? 1 : -1)
    previousScore = previousRelativeScoreLead * (currentPlayer === 'B' ? 1 : -1)

    // Katago has an oddity, which is that the estimated scoreLead of the top suggested move in
    // moveInfos does not generally equal rootInfo.scoreLead. This leads to some minor variations
    // in score reporting where the estimated value of a move changes after being clicked.
    // To mitigate this, we'll define a field moveDelta for suggested moves which is the average
    // of rootInfo.scoreLead ... TODO: finish this

    // Evaluate values of alternative moves of interest:
    previousMovePackage.data.moveInfos.forEach(moveInfo => {
        const absoluteScoreLead = moveInfo.scoreLead * (currentPlayer === 'B' ? 1 : -1)
        moveInfo.moveDelta = (absoluteScoreLead - previousScore) * (currentPlayer === 'B' ? 1 : -1)
      }
    )

    // console.debug(movePackage.data.rootInfo.scoreLead, previousMovePackage.data.rootInfo.scoreLead, absoluteScoreRelativeToCurrentPlayer)

  } else {

    if (parseInt(movePackage.turn) === 0) {
      previousScore = 0 // TODO I think this should actually be modified depending on komi and handicap stones
      previousMaxScoringMove = 0
    }

  }

  return {
      turn: movePackage.turn,
      currentPlayer: currentPlayer,
      blackWinrate: blackWinrate,
      score: absoluteScoreRelativeToCurrentPlayer,
      moveDelta: (absoluteScoreRelativeToCurrentPlayer - previousScore) * (currentPlayer === 'B' ? 1 : -1) ,
      alternativeMoves: !! previousMovePackage ? previousMovePackage.data.moveInfos : [],
    }
}

// For evaluating moves
export const opacityNext = "0.92"
export const opacitySuggestion = "0.7"
export const evaluationColors = (opacity=1) => {

  return {
    'blunder': `rgba(240, 35, 17, ${opacity})`,
    'mistake': `rgba(230, 126, 0,${opacity})`,
    'inaccuracy': `rgba(230, 210, 0,${opacity})`,
    'good': `rgba(51, 204, 89,${opacity})`,
    'great': `rgba(51, 204, 89,${opacity})`,
    'optimal': `rgba(51, 204, 89,${opacity})`,
    'simplest': `rgba(72, 134, 213,${opacity})`
  }
}


function App() {

  // State variables
  const [board, setBoard] = useState(emptyBoard);
  const [boardTree,setBoardTree] = useState({});
  const [state, setState] = useState({}); // game state [gameTrees,gameIndex,gameCurrents]
  const [ghostMap,setGhostMap] = useState(undefined);
  const [markerMap,setMarkerMap] = useState(undefined);
  const [loadingMap,setLoadingMap] = useState(undefined);
  const [nextMovePass, setNextMovePass] = useState(false)

  // Authentication token for communicating with the Django backend
  const [authToken, setAuthToken] = useState(localStorage.getItem("access_token"));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem("refresh_token"));
  const authTokenRef = useRef(authToken)

  // Information about the game
  const [currentMoveNumber, setCurrentMoveNumber] = useState(undefined);
  // const gameIdRef = useRef(gameId);
  // const [gameLength,setGameLength] = useState(null);
  // const gameLengthRef = useRef(gameLength);
  const [handicapStones, setHandicapStones] = useState([]);
  const [treePosition, setTreePosition] = useState(undefined)
  const [initialPlayer,setInitialPlayer] = useState('B')
  const [currentPlayer,setCurrentPlayer] = useState('B')

  const { gameState, server } = useServer();
  const {
    gameLength,
    setGameLength,
    gameLengthRef,
    } = gameState;

  // Variables tracking appearance of the site and preferences
  const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [scoreIsLoading, setScoreIsLoading] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const DEFAULTRIGHTPANELWIDTHINPIXELS = 275

  // Analysis related
  const [analyzedMoves, setAnalyzedMoves] = useState([]);  // Analyzed moves for the graph which is populated asynchronously
  const analyzedMovesRef = useRef(analyzedMoves);
  const [analysisComplete, setAnalysisComplete] = useState(true);  // Record whether analysis is complete
  const analysisCompleteRef = useRef(analysisComplete);
  const [requestedMoves, setRequestedMoves] = useState([])
  const requestedMovesToBeAnalyzedRef = useRef(requestedMoves);
  const [currentlyFetching, setCurrentlyFetching] = useState(false);
  const [waitingOnKatago,setWaitingOnKatago] = useState(false)
  const waitingOnKatagoRef = useRef(waitingOnKatago);
  const [variationIsLoading, setVariationIsLoading] = useState(false);
  const [currentScore, setCurrentScore] = useState(undefined)
  const [nextMoveScore, setNextMoveScore] = useState(undefined)

  // For analyzing variations
  const [inGameVariation, setInGameVariation] = useState(false)
  const inGameVariationRef = useRef(inGameVariation);
  const [variationHashes, setVariationHashes] = useState([])
  const [showBlackAnalysis, setShowBlackAnalysis] = useState(true)
  const [showWhiteAnalysis, setShowWhiteAnalysis] = useState(true)

  // For evaluating moves
  let blunderThreshold = -7
  let mistakeThreshold = -5
  let inaccuracyThreshold = -2
  let goodThreshold = -1
  let greatThreshold = -0.5
  let optimalThreshold = -0.3 // This threshold determines which AI moves are used as suggestions
  const defaultEvaluationThresholds = {
          'blunder': blunderThreshold,
          'mistake': mistakeThreshold,
          'inaccuracy': inaccuracyThreshold,
          'good': goodThreshold,
          'great': greatThreshold, }

  const defaultAnalysisSearchDepth='superficial'

  const [nextMoveEvaluation, setNextMoveEvaluation] = useState(undefined)

  // For uploading SGF files
  const fileInputRef = useRef(null); // Reference to the hidden file input

  // For collapsing the left panel and resizing left/right panels
  const [isCollapsed, setIsCollapsed] = useState(false);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const rightResizerRef = useRef(null);

  // For managing dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showUploadGameDialog, setShowUploadGameDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [stoneMenu, setStoneMenu] = useState({  // right-click menu on stones
    visible: false,
    x: 0,
    y: 0,
    moveData: null,
  });

  const containerRef = useRef(null);
  const [boardDimensions, setBoardDimensions] = useState({ height:0, width: 0 });

  // User info
  const [username, setUsername] = useState("");
  const [preferences, setPreferences] = useState({
    tier: null,
    depth: null,
    evaluationThresholds: defaultEvaluationThresholds,
    rightPanelWidth: null,
    isBackgroundRemoved: false,
    showMarkers: false,
    showCoordinates: false,
  });

  // For getting links
  const location = useLocation();

  // useRefs for getting the current states of variables
  // useEffect(() => { // Moved to ServerContext.js
  //   gameIdRef.current = gameId;
  // }, [gameId]);

  useEffect(() => {
    analyzedMovesRef.current = analyzedMoves;
  }, [analyzedMoves]);

  useEffect(() => {
    analysisCompleteRef.current = analysisComplete;
  }, [analysisComplete]);

  useEffect(() => {
    inGameVariationRef.current = inGameVariation;
  }, [inGameVariation]);

  useEffect(() => {
    requestedMovesToBeAnalyzedRef.current = requestedMoves;
    }, [requestedMoves]);

  useEffect(() => {
    waitingOnKatagoRef.current = waitingOnKatago;
  }, [waitingOnKatago]);

  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  // Subscribe to server on initial render
  useEffect(() => {
    // Callback to update React state when Server data changes
    const handleServerUpdate = (data) => {
      setAnalyzedMoves(data.analyzedMoves)
      setAnalysisComplete(data.analysisComplete)
      setRequestedMoves(data.requestedMoves)
      setCurrentlyFetching(data.currentlyFetching)
      setWaitingOnKatago(data.waitingOnKatago)
      setInGameVariation(data.inGameVariation)
      // setIsLoading(data.isLoading)
      // setCurrentVariation(data.currentVariation)
      // setVariationCurrents(data.variationCurrents)
    };

    // Subscribe to the Server instance
    server.subscribe(handleServerUpdate);

    // Fetch initial data
    // server.fetchData("/katago/games");

    // Cleanup: Unsubscribe when the component unmounts
    return () => {
      server.subscribers = server.subscribers.filter(
        (callback) => callback !== handleServerUpdate
      );
    };
  }, []);

  // Check if authorization token was stored locally and
  // repopulate it if so.
  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
    if (storedToken) {
      setAuthToken(storedToken);
    }

  }, []);

  // For checking if we're logged in
  function loggedIn() {
    return !!authTokenRef.current
  }

  // For getting user preferences on initialization
  async function fetchUserPreferences() {
    try {
      const response = await apiClient.get("/preferences/");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user preferences:", error);
      throw new Error("Failed to fetch user preferences");
    }
  }

  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");
    console.log("From local storage loaded access token:",accessToken)
    console.log("From local storage loaded refresh token:",refreshToken)


    // Load user preferences
    async function loadPreferences() {
      try {
        const userPreferences = await fetchUserPreferences();
        console.debug("Received user preferences from server:", userPreferences);

        const updatedPreferences = {
          ...userPreferences.preferences,
          evaluationThresholds:
            userPreferences.preferences?.evaluationThresholds ||
            defaultEvaluationThresholds,
          depth:
            userPreferences.preferences?.depth || defaultAnalysisSearchDepth,
        };

        setPreferences(updatedPreferences);
      } catch (error) {
        console.error("Failed to load preferences:", error);
      }
    }

    if (accessToken) {
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

      console.log("Fetching username.");
      fetchUserInfo().then(setUsername).catch((err) => {
        console.error("Failed to fetch user info:", err);
      });

      loadPreferences();

      if (location.pathname.startsWith("/login")) {
        navigate("/games");
      }
    } else if (refreshToken) {
      console.log("No access token found, attempting refresh...");
      apiClient
        .post("/token/refresh/", { refresh: refreshToken })
        .then((response) => {
          const newAccessToken = response.data.access;

          console.log("New access token obtained:", newAccessToken);

          // Save the new access token
          localStorage.setItem("access_token", newAccessToken);

          // Set Authorization header for subsequent requests
          apiClient.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${newAccessToken}`;

          // Reinitialize user data and preferences
          fetchUserInfo().then(setUsername).catch((err) => {
            console.error("Failed to fetch user info after refresh:", err);
          });

          loadPreferences();

          if (location.pathname.startsWith("/login")) {
            navigate("/games");
          }
        })
        .catch((error) => {
          console.error("Failed to refresh access token:", error);
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          navigate("/");
        });
    }
    // } else {
    //   console.log("No access token or refresh token available, redirecting to login.");
    //   navigate("/");
    // }
  }, [authToken]);

  // Update the server to let it know the username for its
  // analyze requests to Katago
  useEffect(() => {
    server.data.username = username
  }, [username])

  // For navigating programmatically
  const navigate = useNavigate();

  //////////////////////////////////////////////////////////////
  // Helper functions for altering the appearances of things.
  //////////////////////////////////////////////////////////////

  // Monitoring whether width becomes mobile-sized
  useEffect(() => {
    // Handler to update the state when the window is resized
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Initial check for the current window width
    handleResize();

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // If screen becomes mobile-sized, make the right-panel width 100%
  // to override whatever user-determined value was set before.
  useEffect(() => {
    if(!! rightPanelRef.current) {
      if(isMobile) {
        rightPanelRef.current.style.width = '100%';
      } else if (!! preferences.rightPanelWidth) {
        rightPanelRef.current.style.width = `${preferences.rightPanelWidth}px`;
      } else {
        rightPanelRef.current.style.width = `${DEFAULTRIGHTPANELWIDTHINPIXELS}px`;
      }
    }
  }, [isMobile])

  // Handle resizing the right panel
  const handleRightResize = (e) => {
    const startX = e.clientX;
    const startWidth = rightPanelRef.current.offsetWidth;

    const handleMouseMove = (event) => {

      const newWidth = startWidth - (event.clientX - startX);
      if (newWidth > 200 && newWidth < 400) {
        const updatedPreferences = {
          ...preferences,
          rightPanelWidth: newWidth
        }
        setPreferences(updatedPreferences)
        updateServerPreferences(updatedPreferences)
        rightPanelRef.current.style.width = `${newWidth}px`;

      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Dynamically adjust the dimensions of the board when the panels are resized,
  // the window changes, or the url changes
  useEffect(() => {
    const container = containerRef.current;
    let resizeTimeout;

    const handleResize = (entries) => {
      const { width, height } = container.getBoundingClientRect();
      // Debounce the updates to prevent ResizeObserver loop warning
      clearTimeout(resizeTimeout);
      const newWidth = Math.min(width,height)
      resizeTimeout = setTimeout(() => {
        setBoardDimensions({ width: newWidth, height: newWidth });
      }, 50);
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
      clearTimeout(resizeTimeout);
    };
  }, [location, containerRef, isCollapsed]);

  // useEffect(() => {
  //   const container = containerRef.current;

  //   // Function to handle resizing
  //   const handleResize = () => {
  //     if (!container) return;

  //     const { width, height } = container.getBoundingClientRect();

  //     // Set the board dimensions as the smaller of the width or height
  //     const newWidth = Math.min(width, height);
  //     setBoardDimensions({ width: newWidth, height: newWidth });
  //   };

  //   // Resize observer to monitor changes in container size
  //   const resizeObserver = new ResizeObserver(() => {
  //     handleResize();
  //   });

  //   if (container) {
  //     resizeObserver.observe(container);
  //   }

  //   // Initial resize call to set dimensions
  //   handleResize();

  //   // Cleanup observer on unmount
  //   return () => {
  //     resizeObserver.disconnect();
  //   };
  // }, [containerRef, isCollapsed]);

  useEffect(() => {

    // user is on home page
    if(location.pathname == '/') {
      clearGameTrees()
    }

    // user navigated to a specific game
    if(location.pathname.startsWith('/game/')) {

      // Tell the server we're on a game page
      server.data.onGamePage = true

      const setGame = (gameId, games) => {
        const game = games.find(game => game.info.gameId === gameId)
        if(!game) {
          console.warn("Game with ID not found:", gameId);
            return
        } else { // game with id gameId was found
          console.log('game:',game)
          console.log("setting state")
          setState(game.state)
          }
      }

      // get gameId
      const gameId = location.pathname.split("/")[2]
      console.debug(gameId)

      if (!gameId) {
        console.warn("Invalid game ID in URL");
        return;
      }

      // Set most recent gameId for next time user logs in
      localStorage.setItem("most_recent_game_id", gameId);

      // Fetch all games (without pagination) and set the state for the specific game
      fetchGames(null, null, (games) => setGame(gameId, games), () => {}, () => {}, () => {}, () => {}, true)

      // Set right panel width to user-set width
      if(!! preferences.rightPanelWidth && !! rightPanelRef.current && ! isMobile) {
        rightPanelRef.current.style.width = `${preferences.rightPanelWidth}px`;
      }
    } else { // user not analyzing a specific game
      server.data.onGamePage = false
    }

  },[location])

  // Dynamic styles for the .shudan-goban class
  const gobanStyle = {
    '--shudan-board-border-color': isBackgroundRemoved ? 'white' : '#CA933A',
    '--shudan-board-background-image': isBackgroundRemoved ? 'none' : 'url(./board.png)', // Toggle background image
    '--shudan-board-background-color': isBackgroundRemoved ? 'transparent' : '#F1B458', // Optional: Set background color when image is removed

  };


  // Listener for collapsing debug panel
  const handleDebugCollapseButtonClick = () => {
    const collapseButton = document.getElementById("debug-collapse-button");
    const collapsibleContent = document.getElementById("debug-panel-inner");
    const isHidden = collapsibleContent.classList.toggle("hidden");
}

  // Listeners for managing stone right-click menu
  const handleStoneMenu = (event, moveData) => {
    event.preventDefault();
    event.stopPropagation() // so it doesn't trigger multiple click events
    setStoneMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      moveData: moveData, // Pass any relevant stone-related data here
    });
  };


  const closeStoneMenu = () => {
    setStoneMenu({ visible: false, x: 0, y: 0, moveData: null });
  };

  // Any click outside the menu closes the right-click menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".stone-menu")) {
        closeStoneMenu();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  //////////////////////////////////////////////////////////////
  // Helper functions for managing mouse clicks and buttons.
  //////////////////////////////////////////////////////////////

  // Handle click on log out button
  const handleLogout = () => {

    // Clear tokens from localStorage or sessionStorage
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    // Clear any additional user-specific data
    localStorage.removeItem("user_info");

    setAuthToken(null)

    // TODO: remove tier:null and depth:null since these should be fields in preferences
    setPreferences({tier: null, depth: null, preferences: null})

    // Redirect to the login page
    navigate("/login");
  }

  // Handle click on log in button
  const handleLoginClick = () => {
    const authToken = localStorage.getItem("access_token");

    if (!! authToken) {
      navigate('/games')
    } else {
      navigate('/login')
    }
  }

  // Handle click on get started button
  const handleGetStartedClick = () => {
    const authToken = localStorage.getItem("access_token");

    if(authToken) {
      navigate('/games')
    } else {
      navigate('/signup')
    }
  }

  // Handle mouse events on the board
  const handleVertexMouseUp = (evt, [x, y]) => {

    // Check if the user clicked on the next move on the main line,
    // in which case we simply go to the next move.
    if(currentMoveNumber < gameLength && !server.data.inGameVariation) {
      const nextMove = getNextMove(currentMoveNumber, true)
      if (!!nextMove) {
        const [nextMoveSGF,[nextMoveX,nextMoveY]] = nextMove
        if(!server.data.inGameVariation && nextMoveX === x && nextMoveY === y) {
          goToNextMove()
          return
        }
      }
    }

    const nextPlayer = currentPlayer
    let sign = nextPlayer === 'B' ? 1 : -1

    const vertex = [x, y]
    try {

      // This will throw an error if the move violates
      // a ko, is a suicide, or there is already a stone
      // at that position, cf. board.makeMove() at
      // https://github.com/SabakiHQ/go-board .
      let newBoard = board.makeMove(
        sign,
        vertex,
        { // options
          preventOverwrite:true,
          preventSuicide:true,
          preventKo:true
        }
      );

      let {gameTrees, gameIndex, gameCurrents} = state
      let tree = gameTrees[gameIndex]
      let currents = gameCurrents[gameIndex]

      const dimensions = getGameInfo(tree,currents).size

      server.data.inGameVariation = true
      setNextMovePass(false)

      const newNodeId = treePosition + "|" + vertex[0]+","+vertex[1]
      let newTree = tree.mutate(draft => {
        draft.UNSAFE_appendNodeWithId(treePosition,
          newNodeId, {
        [nextPlayer]: [sgf.stringifyVertex(vertex)]
        })
      })

      gameTrees[gameIndex] = newTree

      // update game tree
      const newState = {
        gameTrees: gameTrees,
        gameIndex: gameIndex,
        gameCurrents: gameTrees.map(_ => ({})),
      }

      const treeInJSON = treeToJSON(newTree, currents, newNodeId);
      const moveId = newNodeId !== null ? treeInJSON.moveId : null;
      const variationHash = moveId;
      const newVariationHashes = variationHashes.concat([variationHash])
      setVariationHashes(newVariationHashes)
      server.data.variationHash = variationHash

      setState(newState)
      server.data.gameId = getGameInfo(newTree,newState.gameCurrents[gameIndex]).gameId
      setCurrentMoveNumber(currentMoveNumber+1)
      setTreePosition(newNodeId)

      // update current player
      setCurrentPlayer(currentPlayer === 'B' ? 'W' : 'B')

      // See if variation has already been analyzed and stored locally
      if (newNodeId in boardTree) {

        const newBoard = boardTree[newNodeId].boardState.clone()

        // The clone() method does not copy ghostStoneMap for some reason,
        // so we need to copy it manually:
        const newGhostStoneMap = boardTree[newNodeId].boardState.ghostStoneMap
        const newLoadingMap = boardTree[newNodeId].boardState.loadingMap
        newBoard.ghostStoneMap = newGhostStoneMap

        markerMap[y][x] = null // reset this marker in case its value was already set
        newBoard.markerMap = markerMap
        newBoard.loadingMap = newLoadingMap

        setBoard(newBoard)
        setGhostMap(newGhostStoneMap)
        setMarkerMap(markerMap)
        setLoadingMap(newLoadingMap)

      } else {

        console.log("did not find variation in boardTree")

        newBoard.ghostStoneMap = blankMap(dimensions)
        newBoard.currentVertex = vertex
        setGhostMap(newBoard.ghostStoneMap)

        markerMap[y][x] = null // reset this marker in case its value was already set
        newBoard.markerMap = markerMap
        setMarkerMap(markerMap)


        // // Don't analyze the move if requested not to
        // if((!showBlackAnalysis && currentPlayer === 'B') || (!showWhiteAnalysis && currentPlayer === 'W')) {

        //   setBoard(newBoard) // update board
        //   boardTree[newNodeId] = { boardState: newBoard, moveNumber: currentMoveNumber+1 }
        //   setBoardTree(boardTree)

        //   return
        // } else {

          // Analyze this new move
          setVariationIsLoading(true)
          newBoard.loadingMap = blankMap(dimensions)
          newBoard.loadingMap[y][x] = true
          setLoadingMap(newBoard.loadingMap)
          setBoard(newBoard)

          boardTree[newNodeId] = { boardState: newBoard, moveNumber: currentMoveNumber+1 }
          setBoardTree(boardTree)

          console.log("preferences from vertexmouseup:",preferences)
          server.analyze(newTree,{},newNodeId,treeInJSON, preferences.depth)
        // }
      }
    } catch(err) {
        console.warn(err)
      }
  };

  // Handle a click on "Game Analysis"
  const handleGameAnalysisClick = () => {
    const gameId = localStorage.getItem("most_recent_game_id");
    if(gameId) {
      navigate(`/game/${gameId}`)
    } else {
      console.warn("No recent game id was set.")
    }
  };

  // Handle a click on "Upload Game"
  const handleUploadGameClick = (event) => {

    if(! !!authToken) {
      navigate('/login')
      return;
    } else {
      setShowUploadGameDialog(true)
    }

  };

  // Handle a click on "Upload SGF"
  const handleUploadSGFClick = (event) => {
    event.stopPropagation() // so it doesn't trigger multiple click events
    console.log("Clicked on upload SGF.")
    if (fileInputRef.current) {
      console.log("Clicking on file input.")
      fileInputRef.current.click();
    }
  }

  async function handleUploadGameSubmit(event) {
    event.preventDefault(); // Prevent page reload

    // Get the value of the textarea
    const SGFSTRING = event.target.elements.sgfString.value;

    await loadSGFString(SGFSTRING, true).then(
      setShowUploadGameDialog(false)
    )
  }

  async function handleOgsUrlSubmit(event) {

    event.preventDefault(); // Prevent page reload

    // Get the value of the textarea
    const gameUrl = event.target.elements.ogsUrl.value;

    await downloadSGFFromOGS(gameUrl)
  }

  async function downloadSGFFromOGS(ogsUrl) {
    try {
      let gameId, sgfUrl;

      // Check if the URL is for a game or a review
      const gameMatch = ogsUrl.match(/\/game\/(\d+)/);
      const reviewMatch = ogsUrl.match(/\/review\/(\d+)/);

      if (gameMatch) {
          gameId = gameMatch[1];
          sgfUrl = `https://online-go.com/api/v1/games/${gameId}/sgf`;
      } else if (reviewMatch) {
          gameId = reviewMatch[1];
          sgfUrl = `https://online-go.com/api/v1/reviews/${gameId}/sgf?without-comments=1`;
      } else {
          console.error("Invalid OGS URL. Must be a game or review URL.");
          return;
      }

      // Fetch the SGF file
      const response = await fetch(sgfUrl);
      if (!response.ok) {
          throw new Error(`Failed to fetch SGF: ${response.statusText}`);
      }

      // Get SGF text
      const SGFSTRING = await response.text();

      await loadSGFString(SGFSTRING, true).then(
        setShowUploadGameDialog(false)
      )

      console.log(`SGF file downloaded for game ${gameId}`);
    } catch (error) {
      console.error("Error:", error.message);
    }
  }

  // Handle a click on "Record New Game"
  const handleRecordNewGameClick = () => {
    console.log("Clicked on record new game.")
    if(!!authToken) {
      navigate('/record')
    } else {
      navigate('/login')
    }

    setShowUploadGameDialog(false)
  }

  // This handles the form where user requests a specific move
  const handleSubmitForGoToMove = (event) => {
    event.preventDefault(); // Prevent form from reloading the page

    const newMoveNumber = document.getElementById('newMoveNumber').value;

    if (newMoveNumber && !isNaN(newMoveNumber)) {
      redrawThenGoToMove(parseInt(newMoveNumber)); // Call goTo with the input value as a number
    } else {
      alert("Please enter a valid move number.");
    }
  }

  //////////////////////////////////////////////////////////////
  // Helper functions for dialogs.
  //////////////////////////////////////////////////////////////

  const openDialog = (message,type) => {
    setDialogMessage(message);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  //////////////////////////////////////////////////////////////
  // Functions which manage game trees and SGF files.
  //////////////////////////////////////////////////////////////

  // Function to load a game tree onto the board
  const loadGameTrees = (gameTrees,gameIndex=0,gameCurrents=null) => {
    let success = true
    try {
      if (gameTrees?.length == 0) throw true
    } catch (err) {
      openDialog('This file is unreadable.', 'warning')
      success = false
    }

    if (success) {
      console.debug("Setting state.");

      let defaultGameCurrents = gameCurrents === null ? gameTrees.map(_ => ({})) : gameCurrents

      const myState = {
        gameTrees: gameTrees,
        gameIndex: gameIndex,
        gameCurrents: defaultGameCurrents,
      }
      let tree = gameTrees[gameIndex]
      let currents = defaultGameCurrents[gameIndex]

      setState(myState)
      server.data.gameId = getGameInfo(tree,currents).gameId

      if (gameTrees?.length > 1) {
        console.warning("Multiple games detected. Haven't implemented functionality to choose which game.");
      }
    }
  };

  const clearGameTrees = () => {
    console.log("Clearing game tree...")
    setState({})
    setBoardTree({})
    setBoard(emptyBoard)
    setCurrentMoveNumber(undefined)
    setTreePosition(undefined)
    setHandicapStones([])
    setMarkerMap(undefined)
    setGhostMap(undefined)
    server.data.gameId = null
    server.data.inGameVariation = false
    server.data.variationHash = null
    setGameLength(null)
    setAnalyzedMoves([])
    setAnalysisComplete(false)
    setVariationIsLoading(false)
    setCurrentScore(null)
    setNextMoveScore(null)
    setNextMoveEvaluation(null)
  }


  // Function to handle SGF file processing
  async function handleSGFUpload(event) {
    const file = event.target.files[0]; // Get the uploaded file

    if (!file) {
      console.error("No file selected");
      return;
    }

    if (!authToken) {
      console.error("Not logged in.");
      navigate('/login');
      return;
    }

    const reader = new FileReader();

    // Read the file content
    reader.onload = async (e) => {
      const SGFSTRING = e.target.result; // SGF content as string

      await loadSGFString(SGFSTRING, true).then(
        setShowUploadGameDialog(false)
      )
    };

    reader.onerror = () => {
      console.error("Error reading the file.");
    };

    reader.readAsText(file); // Read file as text
  }


  /**
   * Extracts all linear paths from root to leaves of a game tree.
   */
  function getLinearPaths(tree) {
    const paths = [];

    function traverse(node, currentPath) {
        // Add the current node to the path
        let newCurrentPath = currentPath.mutate(draft => {
          draft.UNSAFE_appendNodeWithId(node.parentId, node.id, node.data);
          }
        )

        // Get all child variations (branches)
        const children = node.children;

        if (children.length === 0) {
            // If it's a leaf node, add the path to the list
            newCurrentPath.root.data = tree.root.data
            paths.push(newCurrentPath);
            console.log(newCurrentPath)
        } else {
            // Recursively process each child
            children.forEach(child => traverse(child, newCurrentPath));
        }
    }

    // Start traversal from root
    traverse(tree.root, new GameTree());

    return paths;
  }

  function getGameVariations(state) {

    let {gameTrees, gameIndex, gameCurrents} = state
    let tree = gameTrees[gameIndex]

    const variationStates = getLinearPaths(tree).map(
      variationTree =>
      ({
        gameTrees: [variationTree],
        gameIndex: 0,
        gameCurrents: gameTrees.map(_ => ({})),
      })
    )

    console.log("before variation:",tree)

    return variationStates
  }

  async function askUserWhichLinearVariationToUse(gameVariations) {

    const lengthOfVariation = (gameVariation) => {
      const tree = gameVariation.gameTrees[0]
      const currents = gameVariation.gameCurrents[0]
      // console.log(tree)
      return [...tree.listCurrentNodes(currents)].length
    }

    const maxLengthOfVariation = Math.max(
      ...gameVariations.map(variation => lengthOfVariation(variation))
    )
    return gameVariations.find(variation => lengthOfVariation(variation) === maxLengthOfVariation)
  }

  async function loadSGFString(SGFSTRING, uploadToBackend=false) {

    let success = true
    let gameId = null
    let chosenState = null
    let sgfOfChosenState = null

    try {

      // This state potentially has variations in it. We need to
      // find the variations and then ask the user which one they
      // want to use.
      let myState = sgfStringToState(SGFSTRING)

      const gameVariations = getGameVariations(myState) // Get variations

       // Ask user which variation to use
      chosenState = await askUserWhichLinearVariationToUse(gameVariations)

      let {gameTrees, gameIndex, gameCurrents} = chosenState
      let tree = gameTrees[gameIndex]
      let currents = gameCurrents[gameIndex]

      sgfOfChosenState = sgf.stringify([tree.root])

      // Update the viewer with the uploaded SGF
      console.log("SGF parsed successfully:", tree);
      clearGameTrees(); // Clear old game tree
      setState(chosenState)

      gameId = getGameInfo(tree,currents).gameId
      server.data.gameId = gameId
      navigate(`/game/${gameId}`)

      if (gameTrees?.length > 1) {
        console.warning("Multiple games detected. Haven't implemented functionality to choose which game.");
      }
    } catch (err) {
      console.log(err.message)
      openDialog('This game cannot be read.', 'warning')
      success = false
    }

    if (success && !!gameId) {

      if(uploadToBackend) {

        try {

          // Should probably check if this is still active or not and get it refreshed if not
          const storedToken = localStorage.getItem("access_token");

          // Upload the game to the back-end
          const sgfUploadResult = await uploadSGF(storedToken, sgfOfChosenState, gameId);
          console.log('Response:', sgfUploadResult);

        } catch (error) {
        console.error("Error uploading SGF file:", error);
        }
      }

      return chosenState
    }
  }

  // Perform actions that depend on the updated gameTrees
  useEffect(() => {
    if (state.gameTrees) {
      console.debug("Game loaded. Resetting board.")
      let {gameTrees, gameIndex, gameCurrents} = state
      let tree = gameTrees[gameIndex]
      let currents = gameCurrents[gameIndex]
      let myGameInfo = getGameInfo(tree,currents)
      const dimensions = myGameInfo.size
      const gameId = myGameInfo.gameId
      server.data.gameId = gameId
      setGameLength(myGameInfo.gameLength)
      setMarkerMap(blankMap(dimensions))

      // navigate(`/game/${gameId}`)
      redrawThenGoToMove(0)
    }
  }, [state.gameTrees]);

  //////////////////////////////////////////////////////////////
  // Functions which manage game navigation.
  //////////////////////////////////////////////////////////////

  // const currentPlayer = () => {
  //   return initialPlayer === 'B'
  //     ? (currentMoveNumber % 2 === 1 ? 'B' : 'W')
  //     : (currentMoveNumber % 2 === 0 ? 'B' : 'W')
  // }

  // Program arrow keys to navigate the game
  useEffect(() => {

    const handleKeyDown = (event) => {
      if(location.pathname.startsWith('/game/')) {
        switch (event.key) {
          case "ArrowLeft":
            goToPreviousMove();
            break;
          case "ArrowRight":
            goToNextMove();
            break;
          default:
            break;
        }
      }
    };

    // Add the event listener
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [boardTree,state,currentMoveNumber]);

  // Returns to most recent main line move from a variation
  const backToGame = () => {
    let {gameTrees, gameIndex, gameCurrents} = state

    if (gameTrees === undefined) openDialog('No game uploaded.', 'error')
    else {
      if(! server.data.inGameVariation) {
        return
      } else {
        const numVariations = variationHashes.length
        server.data.inGameVariation = false
        setVariationHashes([])
        redrawThenGoToMove(currentMoveNumber-numVariations)
      }
    }
  }

  // Redraws entire board up to a specified move number
  const redrawThenGoToMove = (newMoveNumber) => {

    // console.debug("redrawThenGoToMove() called")
    let {gameTrees, gameIndex, gameCurrents} = state
    let tree = gameTrees[gameIndex]
    let currents = gameCurrents[gameIndex]
    const dimensions = getGameInfo(tree,currents).size

    if (gameTrees === undefined) openDialog('No game uploaded.', 'error')
    else {

      if(server.data.inGameVariation) {
        server.data.inGameVariation = false
        setVariationHashes([])
      }
      if (newMoveNumber === -1) { // go to last move
        newMoveNumber=gameLength
      }

      if (newMoveNumber > gameLength)
        console.warn("Requested move exceeds game length.");

      if (newMoveNumber > 0 && newMoveNumber !== currentMoveNumber) {

            const lastObject=Object.values(boardTree).find(obj => obj.moveNumber === 0);
            let newBoard=lastObject.boardState;
            let newBoardTree=boardTree;

            let i = 0
            let node = tree.root
            let newMarkerMap = blankMap(dimensions)
            while (i < newMoveNumber) {
              try {
                console.debug("Making move.");
                node = tree.navigate(
                  node.id,
                  1, // go forward 1 step
                  gameCurrents[gameIndex]
                )

                // Make move
                // TODO: implement errors for invalid moves (see board.makeMove on @sabaki/go-board)
                let propData = {B: 1, W: -1}
                let vertex = null

                for (let prop in propData) {
                  if (node.data[prop] == null) continue

                  vertex = parseVertex(node.data[prop][0])
                  newBoard = newBoard.makeMove(
                    propData[prop],
                    vertex)
                  newBoard.currentVertex = vertex

                  break
                }

                // Get current move
                const currentMoveData = extractRelevantFieldsByNumber(analyzedMoves[server.data.gameId],i+1)

                // Add color to the *current* move to show its evaluation
                if(!!currentMoveData) {
                  const [currentMoveSGF,[currentMoveX,currentMoveY]] = getNextMove(i)
                  const currentMaxMoveScore = maxMoveDelta(currentMoveData)
                  const currentMaxScoringMoves = maxScoringMoves(currentMoveData)
                  const currentSimplestMaxScoringMoves = simplestMaxScoringMoves(currentMoveData)

                  // Add a marker
                  newMarkerMap[currentMoveY][currentMoveX] = {
                    ...newMarkerMap[currentMoveY][currentMoveX],
                    move: currentMoveSGF,
                    moveData: currentMoveData,
                    alternativeMoves: currentMaxScoringMoves,
                    alternativeSimplestMoves: currentSimplestMaxScoringMoves,
                  }
                }

                newBoard.markerMap = newMarkerMap
                const storedBoard = newBoard.clone()
                storedBoard.markerMap = newMarkerMap

                if (!(node.id in newBoardTree)) { // update board tree
                  newBoardTree[node.id]={boardState: storedBoard, moveNumber: i+1}
                }

                i+=1 // increment

              } catch (err) { // No more (valid) moves

                  break; // break out of the while loop
                //openDialog('Cannot move to next move.', 'error')
              }
            }

            newBoard.markerMap = newMarkerMap

            setMarkerMap(newMarkerMap)
            setBoard(newBoard)
            setBoardTree(newBoardTree)
            setCurrentMoveNumber(i)
            setTreePosition(node.id)

            setCurrentPlayer(
              initialPlayer === 'B'
              ? (i % 2 === 0 ? 'B' : 'W')
              : (i % 2 === 0 ? 'W' : 'B')
            )

        } else if (newMoveNumber === 0) {

          // Check for initial/handicap stones
          let moves = []
          let initialStones = []
          let initialMoves = []
          let initialPlayer = null
          let rootNode = tree.root

          for (let prop of ['B', 'W', 'AB', 'AW']) {
            if (rootNode.data[prop] == null)
              continue
            let color = prop.slice(-1)
            let sign = color === 'B' ? 1 : -1
            let vertices = [].concat(
              ...rootNode.data[prop].map(parseCompressedVertices)
            )

            for (let vertex of vertices) {
              // Convert [2,4] -> "C15" for Katago
              const gtpVertex=HORIZONTALCOORDS[vertex[0]]+String(dimensions[0] - vertex[1])
              const move = [color,gtpVertex]
              if (prop[0] === 'A') {
                initialStones.push(move)
                initialMoves.push([sign,vertex])
              }
              else {
                moves.push(move)
              }
            }
          }

        if (initialStones.length > 0) {

          if (initialStones[initialStones.length-1][0] === 'B') {
            initialPlayer = 'W';
            setInitialPlayer(initialPlayer);
            setCurrentPlayer(initialPlayer);
          }

          if (initialStones.every(stone => stone[0] === 'B')) {
            console.log("Found handicap stones:", initialStones.length)
            setHandicapStones(initialStones)
          }

        }

        let newBoard = Board.fromDimensions(dimensions[0], dimensions[1])

        // Play out the initial stones
        for (let move of initialMoves) {
            newBoard = newBoard.makeMove(move[0], move[1])
          }

        let newBoardTree = boardTree;
        newBoardTree[tree.root.id]={boardState: newBoard, moveNumber: 0}

        setBoard(newBoard)
        setBoardTree(newBoardTree)
        setCurrentMoveNumber(0)
        setTreePosition(tree.root.id)
      }
    }
  };

  // Go to previous move (if possible) without redrawing board
  const goToPreviousMove = () => {

    let {gameTrees, gameIndex, gameCurrents} = state
    let tree = gameTrees[gameIndex]
    let currents = gameCurrents[gameIndex]
    const dimensions = getGameInfo(tree,currents).size

  if (gameTrees === undefined) openDialog('No game uploaded.', 'error')
    else {
      if (currentMoveNumber > 0) {
        try {
          let node = tree.navigate(
            treePosition,
            -1, // go backwards 1 step
            gameCurrents[gameIndex]
          )
          // console.debug("currentVariation:",currentVariation);
          // console.log(currentVariation.length);

          // If we're currently in a variation and moving back by one move,
          // remove the move from the list of moves in the variation
          if (variationHashes.length > 0) {
            if(variationHashes.length === 1) {
              server.data.inGameVariation = false
            }
            const previousVariationHash = variationHashes[variationHashes.length-2]
            server.data.variationHash = previousVariationHash
            setVariationHashes(variationHashes.slice(0,variationHashes.length-1))
          }

          if (node.id in boardTree) {
            const previousLoadingMap = boardTree[node.id].boardState.loadingMap
            const previousBoard = boardTree[node.id].boardState

            if((!showBlackAnalysis && currentPlayer === 'B') || (!showWhiteAnalysis && currentPlayer === 'W')) {
              previousBoard.ghostStoneMap = blankMap(dimensions)
            } else {
              const previousGhostStoneMap = previousBoard.ghostStoneMap
              const previousMarkerMap = previousBoard.markerMap

              setGhostMap(previousGhostStoneMap)
              setMarkerMap(previousMarkerMap)
            }

            previousBoard.loadingMap = previousLoadingMap
            setLoadingMap(previousLoadingMap)

            setCurrentMoveNumber(currentMoveNumber-1)
            setTreePosition(node.id)

            setBoard(previousBoard)
          }
          else { // Previous move has not been generated yet
            console.log("node",node,"not found in boardTree. redrawing...")
            redrawThenGoToMove(currentMoveNumber - 1)
          }

          // update current player
          setCurrentPlayer(currentPlayer === 'B' ? 'W' : 'B')

        } catch (err) {
          console.error(err.message)
        }
      }
    }
  }

  // Go to next move (if possible) without redrawing board
  const goToNextMove = () => {
    let {gameTrees, gameIndex, gameCurrents} = state
    if (gameTrees === undefined) openDialog('No game uploaded.', 'error')
    else { // Game has been uploaded

      if (server.data.inGameVariation) {
        console.warn("Currently in a variation, there is no next move. Could change this functionality to go to the next AI move.")
        return
      }

      let tree = gameTrees[gameIndex]
      let currents = gameCurrents[gameIndex]
      const dimensions = getGameInfo(tree,currents).size

      // if (currentMoveNumber < tree.listNodesVertically(tree.root.id,1,gameCurrents)-1) {
      try {
        let node = tree.navigate(
          treePosition,
          1, // go forward 1 step
          gameCurrents[gameIndex]
        )

        // Make move
        // TODO: implement errors for invalid moves (see board.makeMove on @sabaki/go-board)
        let propData = {B: 1, W: -1}
        let newBoard = null
        let vertex = null

        for (let prop in propData) {
          if (node.data[prop] == null) continue

          vertex = parseVertex(node.data[prop][0])
          newBoard = board.makeMove(
            propData[prop],
            vertex)
          newBoard.currentVertex = vertex

          break
        }

        newBoard.markerMap = markerMap
        setCurrentMoveNumber(currentMoveNumber+1)
        setTreePosition(node.id)

        if (!(node.id in boardTree)) { // update board tree
          boardTree[node.id]={boardState: newBoard, moveNumber: currentMoveNumber+1}
          setBoardTree(boardTree)
        }

        setBoard(newBoard) // update board

        // update current player
        setCurrentPlayer(currentPlayer === 'B' ? 'W' : 'B')

        return node

      } catch (err) { // No more (valid) moves

        console.warn(err.message)

        return null

        //openDialog('Cannot move to next move.', 'error')
      }
    }
  }

  //////////////////////////////////////////////////////////////
  // Helper functions which return game information.
  //////////////////////////////////////////////////////////////

  // Compute the total number of moves
  const totalNumberOfMoves= () => {
    const {gameTrees, gameIndex, gameCurrents} = state
    const tree = gameTrees[gameIndex]
    const nodes = [...tree.listCurrentNodes(gameCurrents[gameIndex])]

    // Note that nodes contains the root node which has no move info,
    // just game info, so we subtract by one
    return nodes.length - 1
  }

  // Get move of next turn, or after the given turn if specified.
  // This is annoyingly complicated because
  // @sabaki/immutable-gametree makes this painful .
  // Returns next move in two forms, e.g. ["B17",[2,1]]
  const getNextMove = (givenTurn = currentMoveNumber, stayOnMainLine=false) => {
    let {gameTrees, gameIndex, gameCurrents} = state
    // Game hasn't been loaded, so don't do anything
    if (state.gameTrees === undefined) return

    let tree = gameTrees[gameIndex]
    let currents = gameCurrents[gameIndex]
    const dimensions = getGameInfo(tree,currents).size

    let node = null

    if(server.data.inGameVariation && !stayOnMainLine) {
      if(givenTurn < currentMoveNumber) {
        let i = currentMoveNumber - givenTurn
        node = gameTrees[gameIndex].navigate( // node for next move
          treePosition,
          -(i-1),
          gameCurrents[gameIndex]
        )
      } else {
        console.warn("Currently in a variation and requested move",givenTurn, ", is greater than or equal to current move.")
        return
      }
    } else {

      const nodes = [...tree.listCurrentNodes(currents)]
      const nodeWithGivenTurn = nodes[givenTurn]

      node = gameTrees[gameIndex].navigate( // node for next move
        nodeWithGivenTurn.id,
        1, // go forward 1 step
        gameCurrents[gameIndex]
      )
    }

    if (! !!node) { // No more moves
      console.log(node)
      console.log("no more moves")
      return null
    }

    // There may be multiple moves if this tree has variations.
    // I think I should probably be filtering this using
    // "gameCurrents".
    let nextMoves = []
    for (let prop of ['B', 'W', 'AB', 'AW']) {
      let color = prop.slice(-1)
      if (node.data[prop] == undefined) { // wrong prop
        continue
      } else if (node.data[prop] == '') { // pass
        return ['pass',[-1,-1]]
      } else {
        let sign = color === 'B' ? 1 : -1
        let vertices = [].concat(
          ...node.data[prop].map(parseCompressedVertices)
        )

        for (let vertex of vertices) {
          // Convert [2,4] -> "B4" for Katago
          const gtpVertex=HORIZONTALCOORDS[vertex[0]]+String(dimensions[0] - vertex[1])
          const move = [prop,gtpVertex]
          nextMoves.push(move)
        }
      }
    }
    const nextMove = nextMoves[0][1] // We'll just show first of the next moves
    const moveX = HORIZONTALCOORDS.indexOf(nextMove[0])
    const moveY = dimensions[1] - parseInt(nextMove.slice(1)) // SGF coordinates count top-down

    return [nextMove,[moveX,moveY]]
  }

  //////////////////////////////////////////////////////////////
  // Showing AI suggestions and evaluating moves with colors
  // occurs in the following useEffect()
  //////////////////////////////////////////////////////////////

  // Finding the highest score among the next moves of interest
  const maxMoveDelta = (moveData) => moveData?.alternativeMoves.length ?
      Math.max(...moveData.alternativeMoves.map(moveInfo => moveInfo.moveDelta)) :
      moveData.moveDelta // If no other moves of interest we take this to mean the provided move was optimal
  const maxScoringMoves = (moveData) => moveData.alternativeMoves.filter(moveInfo =>
    isAdmissibleSuggestion(moveInfo, moveData) &&
    moveInfo.moveDelta-maxMoveDelta(moveData) > optimalThreshold
  )

  // Finding the move with the shortest principal variation
  // (we'll use this as a proxy for measuring the simplicity of a move)
  const shortestPrinVar = (moveData) => Math.min(...maxScoringMoves(moveData).map(moveInfo => moveInfo.pv.length))
  const simplestMaxScoringMoves = (moveData) => maxScoringMoves(moveData).filter(moveInfo =>
    moveInfo.pv.length == shortestPrinVar(moveData)
  )

  const displayAnalysisData = (currentMoveData,nextMoveData,showBlackAnalysis=true,showWhiteAnalysis=true) => {

    if (nextMoveData === null) {
       // analysis has not yet been done yet on this turn
       // so we clear the ghostStoneMap
       //console.debug("Clearing ghost stone map because nextMoveData === null...")
       let ghostStoneMap = undefined
       setGhostMap(ghostStoneMap)
      return
    }

    // Get the ghostStoneMap if it exists, otherwise make a blank one
    let {gameTrees, gameIndex, gameCurrents} = state
    let tree = gameTrees[gameIndex]
    let currents = gameCurrents[gameIndex]
    const dimensions = getGameInfo(tree,currents).size
    //let ghostStoneMap = !! board.ghostStoneMap ? board.ghostStoneMap : blankMap(dimensions)
    let ghostStoneMap = blankMap(dimensions)
    let newMarkerMap = !! markerMap ? markerMap : blankMap(dimensions)

    // console.log("ghostStoneMap:",ghostStoneMap)

    // Get next move
    const signNext = nextMoveData.currentPlayer === 'B' ? 1 : -1
    const [nextMoveSGF,[nextMoveX,nextMoveY]] = getNextMove()
    const currentPlayer  = nextMoveData.currentPlayer

    if((!showBlackAnalysis && currentPlayer === 'B') || (!showWhiteAnalysis && currentPlayer === 'W')) {
      board.ghostStoneMap = blankMap(dimensions)
      board.loadingMap = blankMap(dimensions)

      // const newBoard = board.clone()

      if (nextMoveSGF !== 'pass') {
        setNextMovePass(false)

        // Show next move on the board
        ghostStoneMap[nextMoveY][nextMoveX] = {
          sign: signNext, nextMove: true,
          color: currentPlayer === 'B' ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.7)",
        };

        // console.log("Before:",board.markerMap)

        setGhostMap(ghostStoneMap)

        // Populate move number data for next move
        newMarkerMap[nextMoveY][nextMoveX] = {
          ...markerMap[nextMoveY][nextMoveX],
          playerColor: currentMoveData.currentPlayer,
          moveNumber: currentMoveNumber,
          showAnalysis: false,
        }

        // newBoard.markerMap = markerMap
        setMarkerMap(newMarkerMap)

      } else {
        setNextMovePass(true)
      }

      const loadingMap = blankMap(dimensions)
      setLoadingMap(loadingMap)

      setVariationIsLoading(false)

      const newBoard = {
        ...board,
        markerMap: newMarkerMap,
        ghostStoneMap: ghostStoneMap,
      }
      setBoard(newBoard)

    } else {

      const moveEvaluator = new MoveEvaluator(preferences.evaluationThresholds)

      // add suggested moves to the board
      const nextMaxMoveScore = maxMoveDelta(nextMoveData)
      const nextMaxScoringMoves = maxScoringMoves(nextMoveData)
      const nextSimplestMaxScoringMoves = simplestMaxScoringMoves(nextMoveData)

      nextMaxScoringMoves.forEach(moveInfo => {
        if(moveInfo.move !== "pass") {
          const moveX = HORIZONTALCOORDS.indexOf(moveInfo.move[0])
          const moveY = dimensions[1] - parseInt(moveInfo.move.slice(1)) // SGF coordinates count top-down
          if ((moveX !== nextMoveX) || (moveY !== nextMoveY)) {// Only overlay if we're not overwriting the next move, since that will be rendered below with a different marker
            ghostStoneMap[moveY][moveX] = {sign: signNext,
                                          color: evaluationColors(opacitySuggestion)['optimal'],
                                          moveScore: moveInfo.moveDelta,
                                          complexity: moveInfo?.pv.length,
                                          weight: moveInfo?.edgeWeight}
          }
        }
      });

      nextSimplestMaxScoringMoves.forEach(moveInfo => {
        if(moveInfo.move !== "pass") {
          const moveX = HORIZONTALCOORDS.indexOf(moveInfo.move[0])
          const moveY = dimensions[1] - parseInt(moveInfo.move.slice(1)) // SGF coordinates count top-down
          ghostStoneMap[moveY][moveX] = { sign: signNext,
                                          color: evaluationColors(opacitySuggestion)['simplest'],
                                          moveScore: moveInfo.moveDelta,
                                          complexity: moveInfo?.pv.length,
                                          weight: moveInfo?.edgeWeight }
          }
      });

      // console.debug(nextMaxMoveScore)
      // console.debug(nextMaxScoringMoves)
      // console.debug(nextSimplestMaxScoringMoves)
      // console.debug(nextMoveData)

      // Evaluate next move
      let evaluationNext = null
      let moveInfo = null
      const nextRelativeMoveScore = nextMoveData.moveDelta

      // If the next move is a top move that's also simple, we color it blue.
      if(nextSimplestMaxScoringMoves.some(moveInfo => moveInfo.move === nextMoveSGF)) {
        evaluationNext = "simplest"
      } else {
        evaluationNext = moveEvaluator(nextMoveData)
      }

      // To inform the vertex displayed above the graph we populate this variable
      setNextMoveEvaluation(evaluationNext)

      // If the next move is a top move, we take the weights and complexity
      if(nextMaxScoringMoves.some(moveInfo => moveInfo.move === nextMoveSGF)) {
        moveInfo = nextMaxScoringMoves.find(moveInfo => moveInfo.move === nextMoveSGF)
      }

      // Show next move on the board
      if (nextMoveSGF !== 'pass') {
        setNextMovePass(false)

        ghostStoneMap[nextMoveY][nextMoveX] = {
          sign: signNext, type: `${evaluationNext}_next`, nextMove: true,
          color: evaluationColors(opacityNext)[evaluationNext],
          moveScore: nextMoveData.moveDelta,
          complexity: nextMoveData?.pv?.length,
          weight: nextMoveData?.edgeWeight,
        };

        // console.log("Before:",board.markerMap)

        setGhostMap(ghostStoneMap)

        // Populate move number data for next move
        newMarkerMap[nextMoveY][nextMoveX] = {
            ...newMarkerMap[nextMoveY][nextMoveX],
            playerColor: nextMoveData.currentPlayer === 'W' ? 'B' : 'W',
            moveNumber: currentMoveNumber,
            showAnalysis: true,
          }

        setMarkerMap(newMarkerMap)

        const newBoard = {
          ...board,
          markerMap: newMarkerMap,
          ghostStoneMap: ghostStoneMap,
        }
        setBoard(newBoard)

      } else { // next move is a pass
        setNextMovePass(true)
      }
    }

    // Add color to the *current* move to show its evaluation
    if(currentMoveNumber > 0) {
      const [currentMoveSGF,[currentMoveX,currentMoveY]] = getNextMove(currentMoveNumber - 1)
      if (currentMoveSGF !== 'pass') {
      const currentMaxMoveScore = maxMoveDelta(currentMoveData)
      const currentMaxScoringMoves = maxScoringMoves(currentMoveData)
      const currentSimplestMaxScoringMoves = simplestMaxScoringMoves(currentMoveData)

      // Add a marker
      // const markerStoneMap = board.markerStoneMap ? board.markerStoneMap : blankMap(dimensions)
      if(!! markerMap ) {
        markerMap[currentMoveY][currentMoveX] = {
          ...markerMap[currentMoveY][currentMoveX],
          move: currentMoveSGF,
          moveData: currentMoveData,
          alternativeMoves: currentMaxScoringMoves,
          alternativeSimplestMoves: currentSimplestMaxScoringMoves,
          }
        }
      }
    }

    board.markerMap = markerMap
    setMarkerMap(markerMap) // Only update the stateful marker map with the one carrying move evaluations

    // console.log("After:",board.markerMap)

    setBoard(board)

    const storedBoard = board.clone()
    storedBoard.markerMap = markerMap

    // update board tree
    if(!(treePosition in boardTree)) {
      boardTree[treePosition] = { boardState: storedBoard, moveNumber: currentMoveNumber }
      setBoardTree(boardTree)
    }

  }

  const displayVariationAnalysisData = (showBlackAnalysis=true,showWhiteAnalysis=true) => {

    console.log("displayVariation called.")

    const moveEvaluator = new MoveEvaluator(preferences.evaluationThresholds)

    // myAnalyzedVariation is an array with one object
    const myAnalyzedVariation = analyzedMovesRef.current[server.data.variationHash]

    // get previous move
    let previousMoveHash = null
    if(variationHashes.length === 1) {
      previousMoveHash = server.data.gameId
    } else {
      previousMoveHash = variationHashes[variationHashes.length-2]
    }
    const previousMovePackage = analyzedMovesRef.current[previousMoveHash]?.find(y => y.turn === currentMoveNumber - 1);
    const currentMoveData = extractRelevantFieldsByNumber(myAnalyzedVariation,currentMoveNumber,previousMovePackage)

    if ( !!currentMoveData && currentMoveNumber !== undefined) {

      // console.log(myAnalyzedVariation)
      // console.log("currentMoveData:",currentMoveData)
      // console.log("previousMovePackage:",previousMovePackage)

      // Get the ghostStoneMap if it exists, otherwise make a blank one
      let {gameTrees, gameIndex, gameCurrents} = state
      let tree = gameTrees[gameIndex]
      let currents = gameCurrents[gameIndex]
      const dimensions = getGameInfo(tree,currents).size
      let ghostStoneMap = board.ghostStoneMap ? board.ghostStoneMap : blankMap(dimensions)

      const currentPlayer = currentMoveData.currentPlayer;

      const [currentMoveSGF,[currentMoveX,currentMoveY]] = getNextMove(currentMoveNumber - 1)
      const currentMaxMoveScore = maxMoveDelta(currentMoveData)
      const currentMaxScoringMoves = maxScoringMoves(currentMoveData)
      const currentSimplestMaxScoringMoves = simplestMaxScoringMoves(currentMoveData)
      let evaluationCurrent = null

      // If the next move is a top move that's also simple, we color it blue.
      if(currentSimplestMaxScoringMoves.some(moveInfo => moveInfo.move === currentMoveSGF)) {
        evaluationCurrent = "simplest"
      } else {
        evaluationCurrent = moveEvaluator(currentMoveData)
      }

      // To inform the vertex displayed above the graph we populate this variable
      setNextMoveEvaluation(evaluationCurrent)
      setNextMoveScore(Math.round(10 * (currentMoveData.moveDelta)) / 10)

      // Add a marker
      // const markerStoneMap = board.markerStoneMap ? board.markerStoneMap : blankMap(dimensions)
      markerMap[currentMoveY][currentMoveX] = {...markerMap[currentMoveY][currentMoveX],
        move: currentMoveSGF,
        moveData: currentMoveData,
        alternativeMoves: currentMaxScoringMoves,
        alternativeSimplestMoves: currentSimplestMaxScoringMoves,
      }

      // TODO move board updates to its own useEffect() depending on showBlackAnalysis, showWhiteAnalysis
      if((!showBlackAnalysis && currentPlayer === 'W') || (!showWhiteAnalysis && currentPlayer === 'B')) {
        board.ghostStoneMap = blankMap(dimensions)
        board.loadingMap = blankMap(dimensions)
        setGhostMap(blankMap(dimensions))
        setLoadingMap(blankMap(dimensions))
        setVariationIsLoading(false)
        setBoard(board)
        return
      }

      // Evaluate values of alternative moves of interest:
      const [currentMovePackage] = myAnalyzedVariation;
      const score = currentMovePackage.data.rootInfo.scoreLead * (currentPlayer === 'B' ? 1 : -1)
      currentMovePackage.data.moveInfos.forEach(moveInfo => {
        const absoluteScoreLead = moveInfo.scoreLead * (currentPlayer === 'B' ? 1 : -1)
        moveInfo.moveDelta = (absoluteScoreLead - score) * (currentPlayer === 'B' ? 1 : -1)
        }
      )

      // Now get next move and show suggested moves
      const nextMoveData = {
        currentPlayer: currentPlayer === 'W' ? 'B' : 'W',
        alternativeMoves: currentMovePackage?.data?.moveInfos
      }
      const signNext = nextMoveData.currentPlayer === 'B' ? 1 : -1
      const nextMaxMoveScore = maxMoveDelta(nextMoveData)
      const nextMaxScoringMoves = maxScoringMoves(nextMoveData)
      const nextSimplestMaxScoringMoves = simplestMaxScoringMoves(nextMoveData)

      // console.debug(nextMoveData)
      // console.debug("myAnalyzedVariation",myAnalyzedVariation)
      // console.debug("Next best moves:",nextMaxScoringMoves.map(moveInfo => moveInfo.move))

      nextMaxScoringMoves.forEach(moveInfo => {
        if(moveInfo.move !== "pass") {
          const moveX = HORIZONTALCOORDS.indexOf(moveInfo.move[0])
          const moveY = dimensions[1] - parseInt(moveInfo.move.slice(1)) // SGF coordinates count top-down
          // console.log("Adding to ghostStoneMap:",moveX,moveY)
            ghostStoneMap[moveY][moveX] = {sign: signNext,
                                           color: evaluationColors(opacitySuggestion)['optimal'],
                                           moveScore: moveInfo.moveDelta,
                                           complexity: moveInfo?.pv.length,
                                           weight: moveInfo?.edgeWeight}
        }
      });

      nextSimplestMaxScoringMoves.forEach(moveInfo => {
        if(moveInfo.move !== "pass") {
          const moveX = HORIZONTALCOORDS.indexOf(moveInfo.move[0])
          const moveY = dimensions[1] - parseInt(moveInfo.move.slice(1)) // SGF coordinates count top-down
          ghostStoneMap[moveY][moveX] = { sign: signNext,
                                          color: evaluationColors(opacitySuggestion)['simplest'],
                                          moveScore: moveInfo.moveDelta,
                                          complexity: moveInfo?.pv.length,
                                          weight: moveInfo?.edgeWeight }
          }
      });

      // update board
      const updatedBoard = board.clone(); // Create a new instance so that React detects change
      updatedBoard.ghostStoneMap = ghostStoneMap;
      boardTree[treePosition] = { boardState: updatedBoard, moveNumber: currentMoveNumber }
      updatedBoard.markerMap = markerMap
      setMarkerMap(markerMap) // Only update the stateful marker map with the one carrying move evaluations

      // remove loading spinner by clearing the loadingMap;
      // TODO would be better to only set loadingMap[y][x] = false
      updatedBoard.loadingMap = blankMap(dimensions)

      // Update the state
      setBoard(updatedBoard);
      setLoadingMap(updatedBoard.loadingMap)
      setGhostMap(ghostStoneMap)
      setVariationIsLoading(false)
      setBoardTree(boardTree)

    }  else return null
  }

  const loadMainLineAnalysisData = () => {
    if(!loggedIn()) { // we are not logged in
      return;
    }

    // Game hasn't been loaded, so don't do anything
    if (state.gameTrees === undefined) return

    // In a variation, so don't do anything
    if (server.data.inGameVariation) return

    // Preferences haven't been loaded yet so don't do anything
    if(! !!preferences.depth) return

    console.debug("Displaying analysis on main line.")

    // Get current move
    const currentMoveData = extractRelevantFieldsByNumber(analyzedMoves[server.data.gameId],currentMoveNumber)

    // Get next move. Note that Katago calls the first move turn 0 (cf. convention
    // for field analyzeTurns at https://github.com/lightvector/KataGo/blob/master/docs/Analysis_Engine.md),
    // which is why the next move data corresponds to the current move number,
    // whereas we have chosen to label the empty board as move 0.
    const nextMoveData = extractRelevantFieldsByNumber(analyzedMoves[server.data.gameId],currentMoveNumber+1)

    displayAnalysisData(currentMoveData,nextMoveData,showBlackAnalysis,showWhiteAnalysis)

  }

  // For analyzing moves on the main line
  useEffect(() => {

    loadMainLineAnalysisData()

  },[currentMoveNumber, analyzedMoves, showBlackAnalysis, showWhiteAnalysis, showMarkers]);

  const loadVariationAnalysisData = () => {
    if(!loggedIn()) { // we are not logged in
      return;
    }

    // If we're on the main line, don't do anything
    if (!server.data.inGameVariation) return

    // Only display the variation data if we are finished analyzing

    // Don't do anything if the analysis is not complete,
    // otherwise we will show incomplete analysis data
    if(server.data.variationAnalysisComplete)
      displayVariationAnalysisData(showBlackAnalysis,showWhiteAnalysis)

  }

  // For analyzing moves in a variation off the main line
  useEffect(() => {

    loadVariationAnalysisData()

  },[currentMoveNumber, analyzedMoves, showBlackAnalysis, showWhiteAnalysis, showMarkers]);

  // Update score
  useEffect(() => {

    if(analyzedMoves !== undefined && server.data.gameId in analyzedMoves) {

      if(!server.data.inGameVariation) {

        const currentMoveData = extractRelevantFieldsByNumber(analyzedMoves[server.data.gameId], currentMoveNumber)
        const newScore = roundToNearestHalf(currentMoveData?.score)
        if(!isNaN(newScore)) { // Only update if we have a numeric value
          setCurrentScore(newScore)
          setScoreIsLoading(false)
        } else {
          setScoreIsLoading(true)
        }

        let newNextMoveScore = null
        const nextMoveData = extractRelevantFieldsByNumber(analyzedMoves[server.data.gameId], currentMoveNumber+1)
        if (nextMoveData) {
          // Let's find the highest score among the next moves of interest
          const maxMoveScore = nextMoveData?.alternativeMoves.length ?
          Math.max(...nextMoveData.alternativeMoves.map(moveInfo => moveInfo.moveDelta)) :
          nextMoveData.moveDelta // If no other moves of interest we take this to mean the provided move was optimal

          newNextMoveScore = Math.round(10 *(nextMoveData.moveDelta))/10
        }
        // const newNextMoveScore = roundToNearestHalf(nextMoveData?.score)
        if(! isNaN(newNextMoveScore)) // Only update if we have a non-null value
          setNextMoveScore(newNextMoveScore)

      } else {
        // console.debug("analyzedMoves:",analyzedMoves)
        // console.debug("variationHash:",server.data.variationHash)
        // console.debug("currentMoveNumber:",currentMoveNumber)
        // console.debug("currentMoveData:",currentMoveData)

        const currentMoveData = extractRelevantFieldsByNumber(analyzedMoves[server.data.variationHash], currentMoveNumber)
        let newNextMoveScore = null
        if (currentMoveData) {
          // Let's find the highest score among the next moves of interest
          const currentMaxMoveScore = maxMoveDelta(currentMoveData)
        }

        const newScore = roundToNearestHalf(currentMoveData?.score)

        if(!isNaN(newScore)) { // Only update if we have a numeric value
          setCurrentScore(newScore)
          setScoreIsLoading(false)
        } else {
          setScoreIsLoading(true)
          console.log(newScore,currentMoveData)
        }
      }
    }

  },[analyzedMoves, currentMoveNumber])

  // Clear the marker map displaying move evaluations if the search depth changes
  useEffect(() => {

    board.markerMap = undefined
    board.ghostMap = undefined
    setBoard(board)
    setMarkerMap(undefined)
    setGhostMap(undefined)

    server.data.depth = preferences.depth

    if (server.data.inGameVariation) {
      loadVariationAnalysisData()
    } else {
      loadMainLineAnalysisData()
    }

  },[preferences.depth])

  useEffect(() => {

    // Update right panel width to user-set preference
    if(!! preferences.rightPanelWidth && !! rightPanelRef.current && ! isMobile) {
      rightPanelRef.current.style.width = `${preferences.rightPanelWidth}px`;
    }

    setShowMarkers(preferences.showMarkers)
    setShowCoordinates(preferences.showCoordinates)
    setIsBackgroundRemoved(preferences.isBackgroundRemoved)

  },[preferences])

  const updateServerPreferences = async (updatedPreferences) => {
      // Update server preferences
      try {
        const response = await savePreferences(updatedPreferences); // Use the updated preferences
        console.log("Preferences updated on server:", response);
      } catch (error) {
        console.error("Failed to update preferences:", error);
      }
  }

    // Any click outside the upload game dialog closes it
    useEffect(() => {
        const handleClickOutside = (e) => {
        if (!e.target.closest(".upload-game-dialog")) {
            setShowUploadGameDialog(false);
        }
        };

        if(showUploadGameDialog) {
          document.addEventListener("click", handleClickOutside);
          return () => {
          document.removeEventListener("click", handleClickOutside);
          };
        }
    }, [showUploadGameDialog]);

  return (
    <div className="App">
      <div className="container">
        {/* Left Panel */}
        {location.pathname !== "/" && location.pathname !== "/login" && location.pathname !== "/signup" &&
        <div
          ref={leftPanelRef}
          className={`left-panel ${isCollapsed ? "collapsed" : ""}`} >
          <div className="left-panel-banner-title-wrapper">
            <div className="banner-title">
              <Logo />
            </div>
          </div>
          <div className="menu">
            <h2 style={{ cursor: "pointer" }} onClick={handleUploadGameClick}>
              Add Game
            </h2>
            <h2 style={{ cursor: "pointer" }} onClick={() => navigate('/games')}>
            My Games</h2>
            <input
              type="file"
              accept=".sgf"
              ref={fileInputRef} // Attach the ref to the hidden input
              onChange={handleSGFUpload}
              style={{ display: "none" }} // Hide the input
            />
          </div>
          {/* <div className="form-container">
            <input
              type="text"
              value={question}
              onChange={handleChange}
              placeholder="Ask a question..."
            />
            <button onClick={handleSubmit}>Ask AI</button>
            <div>
            <h2>Answer:</h2>
            <p>{answer}</p>
            </div>
          </div> */}
          {location.pathname.startsWith('/game/') &&
          <AnalysisPanel
            showMarkers={showMarkers} setShowMarkers={setShowMarkers}
            isBackgroundRemoved={isBackgroundRemoved} setIsBackgroundRemoved={setIsBackgroundRemoved}
            showCoordinates={showCoordinates} setShowCoordinates={setShowCoordinates}
            showBlackAnalysis={showBlackAnalysis} setShowBlackAnalysis={setShowBlackAnalysis}
            showWhiteAnalysis={showWhiteAnalysis} setShowWhiteAnalysis={setShowWhiteAnalysis}
            preferences={preferences} setPreferences={setPreferences}
          />
          }
          {!! authToken &&
          <UserPanel
            username={username}
            handleLogout={handleLogout}
            preferences={preferences}
            setPreferences={setPreferences}
            />}
        </div>}
        {/* Vertical Control Bar */}
        {location.pathname !== "/" && location.pathname !== "/login" && location.pathname !== "/signup" &&
        <div
          className="control-bar"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? "Open Panel" : "Collapse Panel"} >
        </div>
        }
        {/* Upload game dialog */}
        {showUploadGameDialog && (
        <div className="dialog-container">
          <div className="dialog-box upload-game-dialog">
            <div className="dialog-box-content">
              {/* <div style="position: absolute; top: 20px; right: 20px; z-index: 10; display: flex; justify-content: flex-end;">
                <img
                    src="/icons/close.svg"
                    onClick={() => setShowUploadGameDialog(false)}
                    alt="Close upload game dialog"
                    style="width: 20px; cursor: pointer; " />
              </div>
              <h2>Upload Game</h2> */}
              <div className="panel panel-button" onClick={handleUploadSGFClick}>
                  <h5>Upload SGF File</h5>
              </div>
              <div className="panel panel-button" onClick={handleRecordNewGameClick}>
                  <h5>Record new game</h5>
              </div>
              <div className="panel panel-option">
                <form className="upload-sgf-form" onSubmit={handleOgsUrlSubmit}>
                  <textarea
                    id="ogsUrl"
                    placeholder={"Enter OGS game url"}
                  />
                  <button type="submit">Upload</button>
                </form>
              </div>
              <div className="panel panel-option">
                <form className="upload-sgf-form" onSubmit={handleUploadGameSubmit}>
                  <textarea
                    id="sgfString"
                    placeholder={"Enter SGF string"}
                  />
                  <button type="submit">Upload</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Main content area */}
        <Routes>
          <Route path="/" element={
              <div className="content-wrapper">
                <div className="home-wrapper">
                  <div className="banner">
                    <div className="banner-title">
                      <Logo />
                    </div>
                    <div className="auth-buttons">
                      <button className="login-button" onClick={() => handleLoginClick()}>
                        Log in</button>
                      <button className="signup-button" onClick={() => navigate('/signup')}>Sign up</button>
                    </div>
                  </div>
                  <div className="main-content">
                    <div className="main-content-home-wrapper">
                      <div className="main-content-home-text-wrapper">
                        <div>
                          <div>
                            <span className="home-title">Delve deeper into your games</span>
                          </div>
                          <span className="home-subtitle">
                            <h2>Sign up and start analyzing.</h2>
                          </span>
                          <div style="height:20px"></div>
                          <div>
                          <form onSubmit={handleGetStartedClick}>
                            <button type="submit">Get started with BetterBaduk</button>
                          </form>
                          </div>
                        </div>
                      </div>
                      <div class="splash">
                        <img style="border-radius:20px" src="/go5.png" alt="" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            } />

          {/* Page for seeing your games */}
          <Route path="/games" element={
            <div className="content-wrapper">
              <div className="main-content">
                <MyGames authToken={authToken} loadSGFString={(file) => loadSGFString(file)} setState={setState} />
              </div>
            </div>
          } />

          {/* Page analyzing game at gameId */}
          <Route path="/game/:gameId" element={
            <div className="content-wrapper">
              {/* Go board */}
              <div className="main-content">
                <div className="player-boxes-container">
                <PlayerBox
                  playerColor = 'B'
                  gameInfo = {state.gameTrees ? getGameInfo(state.gameTrees[state.gameIndex],state.gameCurrents[state.gameIndex])
                                              : undefined} />
                <PlayerBox
                  playerColor = 'W'
                  gameInfo = {state.gameTrees ? getGameInfo(state.gameTrees[state.gameIndex],state.gameCurrents[state.gameIndex])
                                              : undefined} />
                </div>
                <div className="goban-container" ref={containerRef}>
                  <BoundedGoban
                    className="goban"
                    style={gobanStyle}
                    maxWidth={boardDimensions.width}
                    maxHeight={boardDimensions.height}
                    signMap={board.signMap}
                    ghostStoneMap={ghostMap}
                    loadingMap={loadingMap}
                    showCoordinates={showCoordinates}
                    showMarkers={showMarkers}
                    showBlackAnalysis={showBlackAnalysis}
                    showWhiteAnalysis={showWhiteAnalysis}
                    markerMap={markerMap}
                    currentMoveNumber={currentMoveNumber}
                    evaluationThresholds={preferences.evaluationThresholds}
                    inGameVariation={server.data.inGameVariation}
                    stoneMenu={stoneMenu}
                    handleStoneMenu={handleStoneMenu}
                    closeStoneMenu={closeStoneMenu}
                    innerProps={{
                      onContextMenu: (evt) => evt.preventDefault(),
                    }}
                    onVertexMouseUp={handleVertexMouseUp}
                  />
                </div>
                {stoneMenu?.visible &&
                      <StoneMenu
                        x={stoneMenu.x}
                        y={stoneMenu.y}
                        visible={stoneMenu.visible}
                        onClose={closeStoneMenu}
                        moveData={stoneMenu.moveData}
                        goToMove={redrawThenGoToMove}
                      />
                    }
              </div>

              {/* Resizer for Right Panel */}
              {location.pathname !== "/games" &&
              <div
                ref={rightResizerRef}
                className="resizer"
                onMouseDown={handleRightResize} >
              </div>
              }

              {/* Right Panel */}
              {location.pathname !== "/games" &&
              <div ref={rightPanelRef} className="right-panel">
                {/* <div className="player-boxes-container">
                </div> */}
                <div className="navigation-bar-container panel">
                  <NavigationBar
                    goToNextMove={() => goToNextMove()}
                    goToPreviousMove={() => goToPreviousMove()}
                    redrawThenGoToMove={(move) => redrawThenGoToMove(move)} />
                </div>
                <div className="score-box-move-details-container">
                <div className="score-box-container panel">
                  <ScoreBox
                    currentScore={currentScore}
                    scoreIsLoading={scoreIsLoading}
                  />
                </div>
                <div className="move-details-container">
                  <MoveDetails
                    currentMoveNumber={currentMoveNumber}
                    nextMoveEvaluation={nextMoveEvaluation}
                    nextMoveScore={nextMoveScore}
                    scoreIsLoading={scoreIsLoading}
                    nextMovePass={nextMovePass}
                    gameLength={gameLength}
                    inVariation={server.data.inGameVariation}
                    />
                </div>
                </div>
                {server.data.inGameVariation ?
                <div style="align-self: center; margin-top: 20px;">
                  <form onSubmit={() => backToGame()}>
                    <button type="submit">Back to game</button>
                  </form>
                </div> :
                <div className="chart-container panel">
                    <Graph
                    gameId={server.data.gameId}
                    state={state}
                    gameLength={gameLength}
                    currentMoveNumber={currentMoveNumber}
                    setCurrentMoveNumber={setCurrentMoveNumber}
                    redrawThenGoToMove={redrawThenGoToMove}
                    analyzedMoves={analyzedMoves}
                    setAnalyzedMoves={setAnalyzedMoves}
                    analyzedMovesRef={analyzedMovesRef}
                    analysisComplete={analysisComplete}
                    setAnalysisComplete={setAnalysisComplete}
                    analysisCompleteRef={analysisCompleteRef}
                    preferences={preferences} />
                  </div>
              }
              </div>
              }
            </div>
            } />

          <Route path="/record" element={
            <RecordGame
              loadSGFString={loadSGFString}
              style={gobanStyle}
              maxWidth={boardDimensions.width}
              maxHeight={boardDimensions.height}
              showCoordinates={showCoordinates}
              stoneMenu={stoneMenu}
              handleStoneMenu={handleStoneMenu}
              closeStoneMenu={closeStoneMenu}
              innerProps={{
                onContextMenu: (evt) => evt.preventDefault(),
              }}
              containerRef={containerRef}
              rightPanelRef={rightPanelRef}
              rightResizerRef={rightResizerRef}
              handleRightResize={handleRightResize}
              />
            } />

          {/* Page for logging in */}
          <Route path="/login" element={
            <div className="content-wrapper">
              <div className="home-wrapper">
                <div className="banner">
                  <div className="banner-title">
                    <Logo />
                  </div>
                  <div className="auth-buttons">
                    <button className="login-button" onClick={() => handleLoginClick()}>
                      Log in</button>
                      <button className="signup-button" onClick={() => navigate('/signup')}>Sign up</button>
                  </div>
                </div>
                <div className="main-content">
                <Login setAuthToken={setAuthToken} />
                </div>
              </div>
            </div>
          } />

          <Route path="/signup" element={
            <div className="content-wrapper">
              <div className="home-wrapper">
                <div className="banner">
                  <div className="banner-title">
                    <Logo />
                  </div>
                  <div className="auth-buttons">
                    <button className="login-button" onClick={() => handleLoginClick()}>
                      Log in</button>
                      <button className="signup-button" onClick={() => navigate('/signup')}>Sign up</button>
                  </div>
                </div>
                <div className="main-content">
                  <SignUp />
                </div>
              </div>
            </div>
          } />

        </Routes>
      </div>
      {/* Message dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog}>
        <DialogTitle>{"Dialog Title"}</DialogTitle>
        <DialogContent>
          <p>{dialogMessage}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default App;
